import { createSelector } from "@reduxjs/toolkit"
import { Configuration } from "shared/configuration"
import { ConfigurationThingTypeMrn } from "shared/configurationData"
import {
  REMINDER_PERIOD_CONFIG_REF_NAME,
  clampReminderPeriodDays,
} from "shared/reminderSchedule"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { useAppSelector } from "src/app/hooks"
import type { RootState } from "src/app/store"
import { selectMeIsReady, selectTenantId } from "src/features/me/meSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectAllThings } from "src/features/things/thingsSlice"

/**
 * Memoized for the same reason as `useExpirationWarningMinutes`: returns a
 * primitive, so extra store subscriptions cannot cause re-render churn.
 */
const selectReminderPeriodDays = createSelector(
  [
    (state: RootState) => selectAllThings(state),
    (_state: RootState, tenantId?: string) => tenantId,
  ],
  (things, tenantId) => {
    const config = things.find(
      (thing) =>
        thing.thingTypeMrn === ConfigurationThingTypeMrn &&
        thing.tenantId === tenantId &&
        (thing as unknown as Configuration).refName === REMINDER_PERIOD_CONFIG_REF_NAME,
    ) as unknown as Configuration | undefined

    // Same default and 1..7 clamp the scan applies, so the dialog's date and
    // the scan cannot disagree on a row that predates the cap.
    return clampReminderPeriodDays(config?.nVal)
  },
)

/**
 * The tenant's automatic pickup-reminder period, in days (LOCKER-324).
 *
 * Read by the manual-send confirmation so the operator is told when the next
 * automatic reminder is already due before they add one by hand. The fallback
 * matches the server's (`DEFAULT_REMINDER_PERIOD_DAYS`), so a tenant whose
 * config row has not been created yet is shown the date the scan would use
 * rather than nothing.
 */
export function useReminderPeriodDays(): number {
  const tenantId = useAppSelector(selectTenantId)
  const isReady = useAppSelector(selectMeIsReady)

  useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetConfigurationsByTenant,
        "ap.tenantId": tenantId,
        "ap.refNames[0]": REMINDER_PERIOD_CONFIG_REF_NAME,
        tenantId,
      },
    },
    { skip: !tenantId || !isReady },
  )

  return useAppSelector((state) => selectReminderPeriodDays(state, tenantId))
}

export default useReminderPeriodDays
